import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ScienceIcon from "@mui/icons-material/Science";
import { NotificationManager } from "react-notifications";
import TestCodeSelectorDialog from "../common/TestCodeSelectorDialog";
import SlateSelectorDialog from "../common/SlateSelectorDialog";
import {
  SaveOrderedBottle,
  UpdateOrderedBottle,
  GetOrdersByBottlerOrderNumber,
} from "../../services/BottleOrdersService";
import { GetSampleBottles } from "../../services/SamplesMaintenanceService";
import SampleBottleSelectorDialog from "../common/SampleBottleSelectorDialog";
import { SAMPLE_TYPE_OPTIONS } from "./ViewOrderedBottle";

const OrderedBottleDialog = ({
  open,
  onClose,
  onSave,
  bottleData,
  orderNumber,
  accountNumber,
}) => {
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [slateDialogOpen, setSlateDialogOpen] = useState(false);
  const [sampleBottles, setSampleBottles] = useState([]);
  const [sampleBottlesLoading, setSampleBottlesLoading] = useState(false);
  const [sampleDialogOpen, setSampleDialogOpen] = useState(false);

  const formatDateValue = (date) => {
    if (!date) return "";
    return date.split("T")[0];
  };

  const [formData, setFormData] = useState({
    orderNumber: bottleData?.orderNumber || orderNumber || 0,
    parameter: bottleData?.parameter || "",
    sampleBottleName: bottleData?.sampleBottleName || "",
    quantity: bottleData?.quantity || 0,
    preservatives: bottleData?.preservatives || "",
    recordid: bottleData?.recordid || 0,
    cocNum: bottleData?.cocNum || "",
    monitorStart: formatDateValue(bottleData?.monitorStart),
    monitorEnd: formatDateValue(bottleData?.monitorEnd),
    sampleFrequency: bottleData?.sampleFrequency || "",
    waterSystemFacilityName: bottleData?.waterSystemFacilityName || "",
    waterFacilityId: bottleData?.waterFacilityId || "",
    analyteCode: bottleData?.analyteCode || "",
    comment: bottleData?.comment || "",
    ordersOrderNumber: bottleData?.ordersOrderNumber || 0,
    sampleType: bottleData?.sampleType || "",
    identifier: bottleData?.identifier || "",
  });

  // Fetch available orders when dialog opens
  useEffect(() => {
    if (open && orderNumber) {
      fetchAvailableOrders();
    }
  }, [open, orderNumber]);

  const fetchAvailableOrders = async () => {
    setOrdersLoading(true);
    try {
      const response = await GetOrdersByBottlerOrderNumber(orderNumber);
      setAvailableOrders(response?.results || []);
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch available orders."
      );
      setAvailableOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setFormData({
        orderNumber: bottleData?.orderNumber || orderNumber || 0,
        parameter: bottleData?.parameter || "",
        sampleBottleName: bottleData?.sampleBottleName || "",
        quantity: bottleData?.quantity || 0,
        preservatives: bottleData?.preservatives || "",
        recordid: bottleData?.recordid || 0,
        cocNum: bottleData?.cocNum || "",
        monitorStart: formatDateValue(bottleData?.monitorStart),
        monitorEnd: formatDateValue(bottleData?.monitorEnd),
        sampleFrequency: bottleData?.sampleFrequency || "",
        waterSystemFacilityName: bottleData?.waterSystemFacilityName || "",
        waterFacilityId: bottleData?.waterFacilityId || "",
        analyteCode: bottleData?.analyteCode || "",
        comment: bottleData?.comment || "",
        ordersOrderNumber: bottleData?.ordersOrderNumber || 0,
        sampleType: bottleData?.sampleType || "",
        identifier: bottleData?.identifier || "",
      });
    }
  }, [open, bottleData, orderNumber]);

  useEffect(() => {
    if (open) {
      fetchSampleBottles();
    }
  }, [open]);

  const handleSampleBottleSelect = (selected) => {
    if (!selected) return;

    setFormData((prev) => ({
      ...prev,
      sampleBottleName: selected.sampleBottleName?.trim() || "",
    }));

    NotificationManager.success("Sample bottle selected");
  };

  const fetchSampleBottles = async () => {
    try {
      setSampleBottlesLoading(true);

      const response = await GetSampleBottles();

      setSampleBottles(
        (response?.results || [])
          .filter((bottle) => bottle.isActive !== false) // ENB-606: only Active bottles can be ordered
          .map((bottle) => ({
          ...bottle,
          sampleBottleName: bottle.sampleBottleName?.trim() || "",
        }))
      );
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to load sample bottles."
      );
    } finally {
      setSampleBottlesLoading(false);
    }
  };

  const handleSampleBottleChange = (event) => {
    const selectedName = event.target.value?.trim() || "";

    setFormData((prev) => ({
      ...prev,
      sampleBottleName: selectedName,
    }));
  };


  const handleChange = (field) => (event) => {
    const value =
      field === "quantity" || field === "ordersOrderNumber"
        ? parseInt(event.target.value, 10) || 0
        : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const sanitizeDates = (data) => {
    const newData = { ...data };
    const dateFields = ["monitorStart", "monitorEnd"];

    dateFields.forEach((field) => {
      if (newData[field] === "" || newData[field] === undefined) {
        newData[field] = null;
      }
    });

    return newData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const sanitizedData = sanitizeDates(formData);
      const isUpdate = sanitizedData.recordid && sanitizedData.recordid > 0;

      let response;
      if (isUpdate) {
        response = await UpdateOrderedBottle(sanitizedData);
        NotificationManager.success(
          response?.message || "Bottle order updated successfully"
        );
      } else {
        response = await SaveOrderedBottle(sanitizedData);
        NotificationManager.success(
          response?.message || "Bottle order saved successfully"
        );
      }

      if (onSave) {
        onSave(sanitizedData);
      }

      onClose();
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.message ||
        error.message ||
        `Failed to ${formData.recordid ? "update" : "save"} bottle order.`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Handle "Assign Test" button click
  const handleAssignTest = () => {
    setTestDialogOpen(true);
  };

  const handleAssignSlate = () => {
    setSlateDialogOpen(true);
  };

  // Handle test selection from TestCodeSelectorDialog
  const handleTestSelection = (selectedTest) => {
    if (selectedTest) {
      // Map selected test properties to form fields
      setFormData((prev) => ({
        ...prev,
        parameter: selectedTest.parameter || prev.parameter,
        analyteCode: selectedTest.analyteCode || prev.analyteCode,
        sampleBottleName: selectedTest.sampleBottleName || prev.sampleBottleName,
        quantity: selectedTest.bottleqty || prev.quantity,
        preservatives: selectedTest.bottlePreservatives || prev.preservatives,
        cocNum: selectedTest.cocNumber || prev.cocNum,
      }));
      NotificationManager.success("Test information applied to form");
    }
    setTestDialogOpen(false);
  };

  // Handle slate selection from SlateSelectorDialog
  const handleSlateSelection = (selectedSlate) => {
    if (selectedSlate) {
      // Map selected slate properties to form fields      
      setFormData((prev) => ({
        ...prev,
        cocNum: selectedSlate.coc || prev.cocNum,
        sampleBottleName: selectedSlate.sampleBottle || prev.sampleBottleName,
        preservatives: selectedSlate.preservation || prev.preservatives,
      }));
      NotificationManager.success("Slate information applied to form");
    }
    setSlateDialogOpen(false);
  };

  const handleMouseDown = (e) => {
    if (e.target.closest(".MuiDialogTitle-root")) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dragPosition.x,
        y: e.clientY - dragPosition.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDragPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (!open) {
      setDragPosition({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [open]);

  const getSelectedOrderDisplay = () => {
    const selectedOrder = availableOrders.find(
      (order) => order.orderNumber === formData.ordersOrderNumber
    );
    return selectedOrder
      ? `${selectedOrder.orderNumber} - ${selectedOrder.clientName}`
      : "";
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            position: "fixed",
            transform: `translate(${dragPosition.x}px, ${dragPosition.y}px)`,
            transition: isDragging ? "none" : "transform 0.2s",
          },
        }}
        onMouseDown={handleMouseDown}
      >
        <DialogTitle
          sx={{
            cursor: "move",
            userSelect: "none",
            background: "linear-gradient(135deg, #1871CA 0%, #1871CA 100%)",
            color: "white",
          }}
        >
          {bottleData?.recordid ? "Edit Ordered Bottle" : "Add New Ordered Bottle"}
        </DialogTitle>
        <DialogContent dividers>
          {/* Assign Test and Slate Buttons */}
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AssignmentIcon />}
              onClick={handleAssignTest}
              size="small"
            >
              Assign Test
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ScienceIcon />}
              onClick={handleAssignSlate}
              size="small"
            >
              Assign Slate
            </Button>
          </Box>

          <Box component="form" id="bottle-form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Order Number"
                  type="number"
                  value={formData.orderNumber}
                  disabled
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Parameter"
                  value={formData.parameter}
                  onChange={handleChange("parameter")}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sample Bottle"
                  value={formData.sampleBottleName || ""}
                  onClick={() => setSampleDialogOpen(true)}
                  InputProps={{
                    readOnly: true,
                  }}
                  placeholder="Click to select sample bottle"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={handleChange("quantity")}
                  variant="outlined"
                  required
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Preservatives"
                  value={formData.preservatives}
                  onChange={handleChange("preservatives")}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="CofC Description"
                  value={formData.cocNum}
                  onChange={handleChange("cocNum")}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Monitor Start"
                  type="date"
                  value={formData.monitorStart}
                  onChange={handleChange("monitorStart")}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Monitor End"
                  type="date"
                  value={formData.monitorEnd}
                  onChange={handleChange("monitorEnd")}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sample Frequency"
                  value={formData.sampleFrequency}
                  onChange={handleChange("sampleFrequency")}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Location"
                  value={formData.waterSystemFacilityName}
                  onChange={handleChange("waterSystemFacilityName")}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Water Facility ID"
                  value={formData.waterFacilityId}
                  onChange={handleChange("waterFacilityId")}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Analyte Code"
                  value={formData.analyteCode}
                  onChange={handleChange("analyteCode")}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Sample Type"
                  value={(formData.sampleType || "").trim()}
                  onChange={handleChange("sampleType")}
                  variant="outlined"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {/* Keep a legacy value that is not in the list visible */}
                  {formData.sampleType?.trim() &&
                    !SAMPLE_TYPE_OPTIONS.includes(formData.sampleType.trim()) && (
                      <MenuItem value={formData.sampleType.trim()}>
                        {formData.sampleType.trim()}
                      </MenuItem>
                    )}
                  {SAMPLE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Identifier"
                  value={formData.identifier}
                  onChange={handleChange("identifier")}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel id="lab-order-select-label">
                    Lab Order Number
                  </InputLabel>
                  <Select
                    labelId="lab-order-select-label"
                    id="lab-order-select"
                    value={formData.ordersOrderNumber || ""}
                    onChange={handleChange("ordersOrderNumber")}
                    label="Lab Order Number"
                    disabled={ordersLoading || availableOrders.length === 0}
                    displayEmpty
                  >
                    <MenuItem value={0}>
                      <em>None</em>
                    </MenuItem>
                    {availableOrders.map((order) => (
                      <MenuItem key={order.orderNumber} value={order.orderNumber}>
                        {order.orderNumber} - {order.clientName}
                      </MenuItem>
                    ))}
                  </Select>
                  {ordersLoading && (
                    <FormHelperText>Loading orders...</FormHelperText>
                  )}
                  {!ordersLoading && availableOrders.length === 0 && (
                    <FormHelperText>No orders available</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Comment"
                  value={formData.comment}
                  onChange={handleChange("comment")}
                  variant="outlined"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary" disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="bottle-form"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading
              ? "Processing..."
              : bottleData?.recordid
                ? "Update"
                : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Code Selector Dialog */}
      <TestCodeSelectorDialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        multiSelect={false}
        onSelect={handleTestSelection}
      />

      {/* Slate Selector Dialog */}
      <SlateSelectorDialog
        open={slateDialogOpen}
        onClose={() => setSlateDialogOpen(false)}
        multiSelect={false}
        onSelect={handleSlateSelection}
      />

      <SampleBottleSelectorDialog
        open={sampleDialogOpen}
        onClose={() => setSampleDialogOpen(false)}
        sampleBottles={sampleBottles}
        loading={sampleBottlesLoading}
        onSelect={handleSampleBottleSelect}
      />

    </>
  );
};

export default OrderedBottleDialog;